import type { FC, ReactNode } from 'react'
import type { ForkliftModel } from '@/types'

import dynamic from 'next/dynamic'
import { Component } from 'react'
import { cn } from '@/lib/utils'
import { useStore } from '@/lib/store'
import { uiStore } from '@/lib/scene-state'
import { configStore, selectedModel } from '@/lib/configurator'

const SceneCanvas = dynamic(() => import('@/components/scene/scene-canvas'), { ssr: false })

class SceneBoundary extends Component<{ children: ReactNode }, { failed: boolean }> {
    state = { failed: false }

    static getDerivedStateFromError() {
        return { failed: true }
    }

    componentDidCatch(error: unknown) {
        console.error("The 3D view could not start:", error)
        uiStore.set({ sceneFailed: true })
    }

    render() {
        return this.state.failed ? null : this.props.children
    }
}

const words = ["FORQ", "POWER", "LIFT", "SAFETY", "BUILD"]

/** Where the truck sits on wide screens in each chapter, so the backdrop word sits behind it. */
const sides = ['right', 'right', 'left', 'right', 'left', 'left']

const wordClass = 'absolute left-1/2 top-[31%] -translate-x-1/2 -translate-y-1/2 select-none whitespace-nowrap bg-linear-to-b from-white/[0.075] to-white/0 bg-clip-text font-heading font-expanded text-[19vw] font-black leading-none tracking-tight text-transparent transition-opacity duration-700 ease-out landscape:top-1/2 landscape:text-[14vw]'

interface SceneLayerProps {
    models: ForkliftModel[]
}

/** Fixed layer behind the page: glow, chapter word, the 3D canvas and its loader. */
export const SceneLayer: FC<SceneLayerProps> = ({ models }) => {
    const ready = useStore(uiStore, state => state.sceneReady)
    const failed = useStore(uiStore, state => state.sceneFailed)
    const active = useStore(uiStore, state => state.activeChapter)
    const slug = useStore(configStore, state => state.modelSlug)
    const model = selectedModel(models, slug)

    return (
        <div aria-hidden='true' className='pointer-events-none fixed inset-x-0 top-0 z-0 h-lvh overflow-hidden'>
            <div className='absolute inset-0 bg-[radial-gradient(60%_50%_at_50%_34%,oklch(0.86_0.17_86/0.10),transparent_70%)] landscape:bg-[radial-gradient(45%_55%_at_62%_52%,oklch(0.86_0.17_86/0.09),transparent_70%)]' />

            {words.map((word, index) => (
                <span
                    key={word}
                    className={cn(
                        wordClass,
                        sides[index] === 'right' ? 'landscape:left-[66%]' : 'landscape:left-[34%]',
                        active === index ? 'opacity-100' : 'opacity-0'
                    )}
                >
                    {word}
                </span>
            ))}

            {model && (
                <span
                    className={cn(wordClass, 'landscape:left-[34%]', active === 5 ? 'opacity-100' : 'opacity-0')}
                    data-mdlfy-cms={model.id ? `forklift_models:${model.id}:name` : undefined}
                >
                    {model.name}
                </span>
            )}

            <div className={cn('absolute inset-0 transition-opacity duration-[1600ms] ease-out', ready ? 'opacity-100' : 'opacity-0')}>
                <SceneBoundary>
                    <SceneCanvas />
                </SceneBoundary>
            </div>

            <div
                className={cn(
                    'absolute left-1/2 top-[31%] flex -translate-x-1/2 -translate-y-1/2 flex-col items-center gap-4 transition-opacity duration-700 landscape:left-[66%] landscape:top-1/2',
                    ready || failed ? 'opacity-0' : 'opacity-100'
                )}
            >
                <div className='relative h-px w-40 overflow-hidden bg-white/10'>
                    <span className='absolute inset-y-0 left-0 w-1/3 animate-loader-bar bg-brand' />
                </div>
                <span className='font-mono text-[11px] uppercase tracking-[0.22em] text-white/45'>
                    Warming up the motors
                </span>
            </div>

            <div className='absolute inset-0 bg-[radial-gradient(120%_90%_at_50%_45%,transparent_55%,oklch(0.12_0.004_255/0.85)_100%)]' />
        </div>
    )
}
